/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */

import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'; // ajax library
import moment from 'moment';
import _ from "lodash";
import axiosDefaults from 'axios/lib/defaults';
import {uuid} from 'uuidv4';

import storeUtils from './utils'
import {
    generateCacheKey,
    cacheNewsData,
    getCachedNews,
    cleanupExpiredCacheEfficient,
    clearAllCacheEntries
} from '@/utils/cacheHelpers';
import broadcastCategoryDescriptions from '@/content/broadcastCategoryDescriptions'
import frequentlyAskedQuestions from '@/content/frequentlyAskedQuestions'

Vue.use(Vuex)

function dateHuman(dtString) {
    const dt = new Date(dtString);
    return `
    ${dt.getDate().toString().padStart(2, '0')}/${(dt.getMonth() + 1).toString().padStart(2, '0')}/${dt.getFullYear().toString().padStart(4, '0')} ${dt.getHours().toString().padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}
  `
}

let lastNewsUpdate = 0

const broadcastMap = {
    "1": {
        name: "PC",
        shortName: "pc",
        order: 1,
        email: "pc-broadcast@thebrokersite.com",
        path: "pc",
    },
    "2": {
        name: "Server/Storage",
        shortName: "server",
        shortNameForList: "Server",
        order: 4,
        email: "server-broadcast@thebrokersite.com",
        path: "server-storage"
    },
    "4": {
        name: "Networking/Telecom",
        shortName: "networking",
        shortNameForList: "Networking",
        order: 5,
        email: "networking-broadcast@thebrokersite.com",
        path: "networking-telecom"
    },
    "5": {
        name: "Printer",
        shortName: "printer",
        order: 8,
        email: "printer-broadcast@thebrokersite.com",
        path: "printer",
    },
    "6": {
        name: "Software",
        shortName: "software",
        order: 12,
        email: "software-broadcast@thebrokersite.com",
        path: "software"
    },
    "7": {
        name: "Laptop",
        shortName: "laptop",
        order: 2,
        email: "laptop-broadcast@thebrokersite.com",
        path: "laptop",
    },
    "8": {
        name: "Electronics",
        shortName: "electronics",
        order: 9,
        email: "electronics-broadcast@thebrokersite.com",
        path: "electronics"
    },
    "10": {
        name: "POS",
        shortName: "pos",
        order: 11,
        email: "pos-broadcast@thebrokersite.com",
        path: "pos"
    },
    "11": {
        name: "Mobile",
        shortName: "mobile",
        order: 6,
        email: "mobile-broadcast@thebrokersite.com",
        path: "mobile"
    },
    "12": {
        name: "Components",
        shortName: "components",
        order: 3,
        email: "components-broadcast@thebrokersite.com",
        path: "components"
    },
    "13": {
        name: "Monitor",
        shortName: "monitor",
        order: 10,
        email: "monitor-broadcast@thebrokersite.com",
        path: "monitor"
    },
    "14": {
        name: "Tablet",
        shortName: "Tablet",
        order: 7,
        email: "tablet-broadcast@thebrokersite.com",
        path: "tablet"
    },
    "15": {
        name: "Gaming",
        shortName: "gaming",
        order: 13,
        email: "gaming-broadcast@thebrokersite.com",
        path: "gaming"
    },
}

Object.values(broadcastMap).forEach((entry) => {
    if (broadcastCategoryDescriptions[entry.path]) {
        entry.description = broadcastCategoryDescriptions[entry.path]
    }
})

export default new Vuex.Store({
    getters: {
        isEU: (state) => state.user && state.user.isEU,
        isSpecialUser: (state) => state.user && [1560, 24116].includes(state.user.id),
        isFirstLoad: () => { //used for setting cookie message on first load
            const isFirstLoad = !localStorage.getItem('isLoadedBefore')
            localStorage.setItem('isLoadedBefore', true)
            return isFirstLoad
        },
        broadcastsEnriched: (state, getters) => {
            return getters.broadcastEnrichedFN(state.broadcasts)
        },
        broadcastEnrichedFN: state => broadcasts => {
            return _.map(broadcasts, (item, i) => {
                return _.extend({
                    index: i,
                    dateAsString: moment(item.date).format('D MMM YYYY'),
                    dateAsStringLong: moment(item.date).format('D MMM YYYY HH:mm:ss'),
                    broadcastListName: state.broadcastMap[item.broadcastList]?.shortNameForList || state.broadcastMap[item.broadcastList]?.name,
                    isOwn: state.user && +item.companyId === +state.user.companyId,
                    isOkToResend: Date.now() - new Date(item.date).getTime() > (3 * 24 * 60 * 60 * 1000), // 3 days
                }, item)
            })
        },
        partsEnriched: state => { //NOTE: this is an array of objects. One for each query. This allows multiple part queries at the same time

            let conversion = 1;
            if (state.currency === "USD") {
                conversion = state.currencyMap.USD;
            } else if (state.currency === "GBP") {
                conversion = state.currencyMap.GBP;
            }

            const arr = [];
            _.each(state.parts, catOrig => {
                const cat = _.cloneDeep(catOrig);
                cat.results = _.map(cat.results, item => _.extend({
                    priceLocalCurrency: item.price === "CALL" ? "CALL" : Math.round(item.price * conversion),
                }, item))
                arr.push(cat)
            })

            return arr;
        },
        companiesEnriched: state => {
            return _.map(state.companies, item => _.extend({
                // Nothing yet
            }, item))
        },

        servicesEnriched: state => {
            return _.map(state.services, item => _.extend({
                // Nothing yet
            }, item))
        },

        newsEnriched: state => {
            return _.map(state.news, item => {
                let bodyStart;
                let doReadMore = false;

                // If there's an <img> tag, include it plus next 350 chars
                // const imgMatch = item.body.match(/<img[^>]+src="([^"]+)"/); // old 

                if (item.thumbnail_image) {
                    bodyStart = (item.body || '').substring(0, 350) + ' ...';
                    doReadMore = true;
                } else {
                    const imgMatch = item.body?.match(/<(img|iframe)[^>]+(src="[^"]+")/); // new, iframe supported
                    if (imgMatch) {
                        const fullImgTag = `
                            <div style="width: 100%; height: 400px; overflow: hidden; display: flex; align-items: center;">
                                ${imgMatch[0].replace(
                                    '<img',
                                    '<img style="width: 100%; height: auto; display: block;"'
                                )}
                            </div>
                        `;
                        const imgTagEnd = item.body.indexOf('>', imgMatch.index) + 1;
                        const afterImgContent = item.body.substring(imgTagEnd, imgTagEnd + 350);
                        bodyStart = fullImgTag + afterImgContent + ' ...';
                        doReadMore = true;
                    } else {
                        // Otherwise just take the first 350 chars
                        if (item.body?.length > 350) {
                            bodyStart = item.body?.substring(0, 350) + ' ...';
                            doReadMore = true;
                        }
                    }
                }

                // If shorter than limit, show all
                if (!bodyStart) {
                    bodyStart = item.body || '';
                }

                return _.extend({
                    catName: state.categoryMap["" + item.catId],
                    bodyHtml: (() => {
                        let cleaned = bodyStart;

                        // Remove empty tags
                        // cleaned = cleaned.replace(/<\s*(\w+)[^>]*>\s*<\/\s*\1>/g, ''); // old 
                        cleaned = cleaned.replace(/<\s*(?!iframe)(\w+)[^>]*>\s*<\/\s*\1>/g, ''); // new, iframe supported

                        // Strip out blank lines
                        cleaned = cleaned.split('\n').filter(line => line.trim() !== '').join('\n');
                        // Convert newlines to <br> (keep tables intact)
                        cleaned = cleaned.replace(/(<table[\s\S]*?<\/table>)|(?:\r\n|\r|\n)/g, (match, table) => table || '<br>');
                        // Fix relative image paths
                        cleaned = cleaned.replace(/images\//g, '../../images/');

                        return cleaned;
                    })(),
                    doReadMore,
                }, item);
            });
        },


        newsPostEnriched: state => {
            if (!state.newsPost) return state.newsPost;

            // If post is after 2025-04-24 then bypass processing
            if (new Date(state.newsPost.date) > new Date('2025-04-24T00:00:00.000Z')) {
                return _.extend({
                    catName: state.categoryMap['' + state.newsPost.catId],
                    bodyHtml: state.newsPost.body
                }, state.newsPost);
            }

            // Otherwise clean up HTML
            const original = state.newsPost.body;
            let cleaned = original.replace(/<\s*(\w+)[^>]*>\s*<\/\s*\1>/g, '');
            cleaned = cleaned
                .split('\n')
                .filter(line => line.trim() !== '')
                .join('\n');
            cleaned = cleaned.replace(/(<table[\s\S]*?<\/table>)|(?:\r\n|\r|\n)/g,
                (match, table) => table || '<br>');
            cleaned = cleaned.replace(/images\//g, '../../images/');

            return _.extend({
                catName: state.categoryMap['' + state.newsPost.catId],
                bodyHtml: cleaned
            }, state.newsPost);
        },


        countriesOptions: state => {
            const countryOptions = _.map(state.countries, countryName => ({value: countryName, name: countryName}));
            return [{value: "", name: "Any Country"}].concat(countryOptions)
        },

        countriesOptionsPleaseSelect: state => {
            const countryOptions = _.map(state.countriesAll, countryName => ({value: countryName, name: countryName}));
            return [{value: "", name: "Please Select Country"}].concat(countryOptions)
        },

        countriesReverseCharge: () => {
            return [
                "Austria",
                "Belgium",
                "Bulgaria",
                "Cyprus",
                "Czech Republic",
                "Denmark",
                "Estonia",
                "Finland",
                "France",
                "German Democr. Rep.",
                "Germany, Fed. Rep. Of",
                "Greece",
                "Hungary",
                "Ireland",
                "Italy",
                "Latvia",
                "Lithuania",
                "Luxembourg",
                "Malta",
                "Poland",
                "Portugal",
                "Romania",
                "Slovakia",
                "Slovenia",
                "Spain",
                "Sweden",
            ]
        },

        markersEnriched: state => {
            return _.map(state.markers, item => _.extend({
                position: {
                    lat: item.lat,
                    lng: item.lng,
                },
                infoText: `<div><span class='font-semibold'>${item.name}</span><br /><a class="text-red-700 mt-2 hover:underline inline-block" href="/companies/${item.id}" target="_blank" rel="noopener">More info...</a></div>`
            }, item))
        },

        savedSearches: state => {
            return _.map(state.savedSearches, (ss, i) => _.extend({
                index: i,
                created: moment(ss.createdDT).format('YYYY-MM-DD'),
                lastHit: ss.lastHitDT ? moment(ss.lastHitDT).fromNow() : '-',
                doUpdateImmediately: !ss.isDigest,
                search: _.compact([ss.q ? `Query: ${ss.q}` : null, ss.broadcastType, ss.listType ? `List: ${state.broadcastMap[ss.listType]?.name}` : `All lists`]).join(" - ")
            }, ss)); // No enrichment here (yet)
        },

        companyNameMap: state => {
            return _.reduce(state.markers, (agg, item) => {
                agg[item.id] = item.name
                return agg;
            }, {})
        },
        bannersEnriched: state => {
            return state.banners;
        },
        inventoryEnriched: state => {
            return _.map(state.inventory, inv => _.extend({}, inv, {price: inv.price ? inv.price : "CALL"}))
        },
        inventoryHitsEnriched: state => {
            return _.map(state.inventoryHits, hit => {
                hit.dateHuman = dateHuman(hit.logdate)
                if (!hit.username) return _.extend(hit, {name: "Guest"});
                if (!hit.companyname) return _.extend(hit, {name: "Former TBS Member"});
                return _.extend(hit, {name: hit.companyname});
            })
        },
        userPicCSS: state => {
            return {
                background: `url('${state.profilePic}') no-repeat right center / cover`,
            }
        },
        companyName: (state) => {
            if (state.user)
                return state.user.name;
        },
    },
    state: {
        expandedCatId: '',
        categories: [
            {id: 1, name: 'Data Erasure Software'},
            {id: 2, name: 'Keyboard Re-printing'},
            {id: 3, name: 'Packaging'},
            {id: 4, name: 'Transport'},
            {id: 7, name: 'Auditing Software'},
            {id: 8, name: 'Licensing'},
            {id: 9, name: 'B2C Platforms'},
            {id: 10, name: 'Hardware Refurbishment'},
            {id: 11, name: 'ERP Solutions'},
            {id: 12, name: 'IT Asset Disposition'},
            {id: 13, name: 'Skins Production & Sales'},
            {id: 14, name: 'Mobile Diagnostics'},
            {id: 15, name: 'WEEE Recycling'},
            {id: 16, name: 'Media'},
        ],
        hideCookiesPopup: false,
        pageSize: localStorage.getItem('pageSize') ? +localStorage.getItem('pageSize') : 10,
        currencyMap: {
            USD: 1,
            GBP: 1,
        },
        currency: "EUR",
        categoryMap: {
            "8": "Bad Debtors",
            "2": "European Broker Meeting",
            "1": "Fraud warnings",
            "3": "Members",
            "4": "The Broker Site",
            "9": "Blog",
        },
        settingsMap: {},
        broadcastSearchError: "",
        broadcastSearchOptions: { profile: "legacy", showSortDropdown: false, sort: "best" },
        nrBroadcasts: 0,
        nrParts: 0,
        nrCompanies: 0,
        nrTraders: 0,
        nrCountries: 0,
        nrInventoryTotal: 0,
        nrInventoryHitsTotal: 0,
        nrNewsItems: 0,
        numFreshNews: localStorage.getItem("newsUnread"),

        broadcasts: [],
        parts: [],
        companies: [],
        countries: [], //loaded once
        countriesAll: [], //for register / freeTrial
        markers: [], //loaded once
        testimonials: [],  //TBS testimonials
        banners: [],
        inventory: [],
        inventoryHits: [],
        news: [],
        newsPayload: '',
        newsPost: {body: ''},

        savedSearches: [],
        user: null,
        profilePic: null,

        lastSearch: {
            companies: {},
            parts: {},
            broadcasts: {},
            inventory: {}
        },

        invoiceStatus: {},
        invoices: [],
        creditNotes: [],
        invoicesEBM: [],
        contactStatus: {}, //contains 'freeTrialBlock' / 'Free Trial Footer' / 'modalMessage' / 'contact_name'
        isModalMessageDismissed: false,
        isInvoiceOverdueMessageDismissed: false,

        doUnapprovedRecsExist: false,

        // isLoggedIn: true, //TODO: deprecate in favor of user
        broadcastMap,
        typeMap: {
            'want-to-buy': 'WTB',
            'wtb': 'WTB',
            'want-to-sell': 'WTS',
            'wts': 'WTS',
        },
        featureMap: {
            'TAWK_CHAT': {name: 'TBS Chat service', featureId: '1'},
            'INVENTORY_ADD': {name: 'Adding Inventory', featureId: '2'},
            'INVOICE_SENDING': {name: 'Sending Invoice', featureId: '3'},
            'INVENTORY_UPLOADER': {name: 'Inventory Uploader', featureId: '4'},
            'BROADCAST_CREATE': {name: 'Broadcast Create', featureId: '5'},
            'BROADCAST_RESEND': {name: 'Broadcast Resend', featureId: '6'}
        },
        frequentlyAskedQuestions,
        regions: [
                { value: "Africa", name: "Africa" },
                { value: "Americas", name: "Americas" },
                { value: "Asia", name: "Asia" },
                { value: "Australia", name: "Australia" },
                { value: "Europe", name: "Europe" }
        ],
        tbsSpecialUsers: ['1563', '1564', '13415', '1560', '15566', '16319', '20849', '24116'],
        isFetchingNews: false,
        isNewsSummary: false,
        hasLoadedFirstPart: false,
        hasLoadedSecondPart: false,
        purchaseOrders: [],
        nrPurchaseOrders: 0,
        fetchingPurchaseOrders: false
    },
    mutations: {
        setExpandedCategory(state, categoryId) {
            state.expandedCatId = categoryId;
        },
        doHideCookiesPopup(state) {
            state.hideCookiesPopup = true
        },
        updateSettings(state, settingsMap) {
            state.currencyMap = {
                USD: +settingsMap.USD,
                GBP: +settingsMap.GBP,
            };
            state.settingsMap = settingsMap;
        },
        changeCurrency(state, newCurrency) {
            state.currency = newCurrency;
        },
        updateBanners(state, banners) {
            state.banners = banners;
        },
        /** Stores a failure separately from empty success. @param {Object} state Vuex state. @param {string} message Failure text, or empty to clear. @returns {void} */
        updateBroadcastSearchError(state, message) {
            // Keep the failure available to the broadcast screen until the next request succeeds.
            state.broadcastSearchError = message
        },

        /** Stores authoritative Search capabilities, defaulting safely for older servers. @param {Object} state Vuex state. @param {Object} [options] Server capabilities. @returns {void} */
        updateBroadcastSearchOptions(state, options) {
            // An older server cannot leave a previously enabled selector visible.
            state.broadcastSearchOptions = options || { profile: "legacy", showSortDropdown: false, sort: "best" }
        },

        updateBroadcasts(state, {results, numResults}) {
            state.broadcasts = results;
            state.nrBroadcasts = numResults
        },
        updateParts(state, results) {
            state.parts = results;
            if (results.length) {
                state.nrParts = results[0].numResults
            }
        },
        setModalMessageDismissed(state) {
            state.isModalMessageDismissed = true;
        },
        setInvoiceOverdueMessageDismissed(state) {
            state.isInvoiceOverdueMessageDismissed = true;
        },
        updateInventory(state, {results, numTotalResults}) {
            state.inventory = results;
            state.nrInventoryTotal = numTotalResults
        },

        updateInventoryHits(state, {results, numTotalResults}) {
            state.inventoryHits = results;
            state.nrInventoryHitsTotal = numTotalResults
        },

        updateCompanies(state, {results, numResults, numTraders, numCountries}) {
            state.companies = results;
            state.nrCompanies = numResults
            state.nrTraders = numTraders
            state.nrCountries = numCountries
        },
        updateNews(state, {results, numResults, isSecondPart = false}) {
            if (isSecondPart) {
                const existingResults = state.news || [];
                const newResults = results || [];
                state.news = [...existingResults, ...newResults];

            } else {
                state.news = results;
            }
            state.nrNewsItems = numResults;
        },
        updateNewsPayload(state, payload) {
            state.newsPayload = payload;
        },
        updateIsFetchingNews(state, isFetching) {
            state.isFetchingNews = isFetching
        },
        updateIsNewsSummary(state, isSummary) {
            state.isNewsSummary = isSummary
        },
        updateHasLoadedFirstPart(state, hasLoadedFirstPart) {
            state.hasLoadedFirstPart = hasLoadedFirstPart
        },
        updatehasLoadedSecondPart(state, hasLoadedSecondPart) {
            state.hasLoadedSecondPart = hasLoadedSecondPart
        },
        updateIsFetchingNe(state, isFetching) {
            state.isFetchingNews = isFetching
        },
        updateNewsPost(state, result) {
            state.newsPost = result
        },
        updateCountries(state, { results } = {}) {
            state.countries = results;
        },
        updateCountriesAll(state, results) {
            state.countriesAll = results;
        },
        updateMarkers(state, {results}) {
            state.markers = results;
        },
        updateSavedSearches(state, {results}) {
            state.savedSearches = results;
        },
        updateTestimonials(state, payload) {
            const sorted = [...payload].sort((a, b) =>
                (b.description?.length || 0) - (a.description?.length || 0)
            );

            // Get first 6 entries as longer testimonials
            const longerTestimonials = sorted.slice(0, 6);

            // Get the remaining entries as shorter testimonials
            const shorterTestimonials = sorted.slice(6);

            // Shuffle each group randomly
            const shuffleArray = (array) => {
                for (let i = array.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [array[i], array[j]] = [array[j], array[i]];
                }
                return array;
            };

            // Combine with longer testimonials first, then shorter ones
            state.testimonials = [
                ...shuffleArray(longerTestimonials),
                ...shorterTestimonials
            ];
        },
        bootstrapUserFromLocalStorage(state) {

            const userStr = localStorage.getItem('user');
            if (!userStr) return;

            const user = JSON.parse(userStr)

            state.user = user;
            axiosDefaults.headers.Authorization = `Bearer ${user.token}`
        },

        // Called on app startup + login
        updateUser(state, user) {

            if (user.doLogout) return this.dispatch("signout")

            // const trustedSig = user.isGold ? "(Gold Member)" : (user.isTrusted ? "(Trusted Member)" : ""); // we have to replace this to include the new Diamond Member Status
            const trustedSig = user.isDiamond ? "(Diamond Member)" : (user.isGold ? "(Gold Member)" : (user.isTrusted ? "(Trusted Member)" : ""));
            user.signature = `\n\n${user.name}, ${user.function}\n${user.companyName} ${trustedSig}\n${user.website}\n${user.address}, ${user.country}\nTelephone: ${user.tel} \n\n\n`;
            localStorage.setItem('user', JSON.stringify(user))
            localStorage.setItem("lastUserID", user.id)

            state.user = user;
            axiosDefaults.headers.Authorization = `Bearer ${user.token}`
        },

        removeUser(state) {
            localStorage.removeItem('user')
            state.user = null;
            delete axiosDefaults.headers.Authorization
        },
        updateProfilePic(state, profilePicBase64) {
            localStorage.setItem('profilePic', profilePicBase64)
            state.profilePic = profilePicBase64;
        },
        removeProfilePic(state) {
            localStorage.removeItem('profilePic')
            state.profilePic = null;
        },

        updateNewsUnread(state, newsUnread) {
            if (newsUnread === undefined || !Number.isInteger(newsUnread)) {
                return;
            }            
            state.numFreshNews = newsUnread;
            if(newsUnread != 0){
                localStorage.setItem('newsUnread', newsUnread);
            }
        },

        updateLastInvoiceStatus(state, status) {
            if (!status) return; // unauth
            state.contactStatus = _.omit(status, ['invoiceDTO'])
            state.invoiceStatus = status.invoiceDTO || {}
        },

        updateInvoices(state, invoices) {
            state.invoices = invoices
        },
        updateCreditNotes(state, creditNotes) {
            state.creditNotes = creditNotes
        },
        updateInvoicesEBM(state, invoices) {
            state.invoicesEBM = invoices
        },
        updatePurchaseOrders(state, {results, numResults}) {
            state.purchaseOrders = results;
            state.nrPurchaseOrders = numResults
        },
        updateFetchingPurchaseOrders(state, status) {
            state.fetchingPurchaseOrders = status;
        },
    },
    actions: {
        getTopSearches({commit}, payload = {}) {
            return axios.get(`/api/misc/topsearches`).then(({data}) => ({
                data: data.payload,
                status: data.status
            }));
        },
        getSettings({commit, dispatch}, payload = {}) {
            return axios.get(`/api/misc/settings`).then(({data}) => {
                commit('updateSettings', data.payload);
                return dispatch('checkNewsCacheValidity').catch(error => {
                    console.error('Cache validity check failed:', error);
                });
            });
        },
        getNewsUnread({state, commit}) {
           
            commit('bootstrapUserFromLocalStorage')         
            if (!axiosDefaults.headers.Authorization) {
                return;
            }
            return axios.get(`/api/inventory/unreadNews`).then(({data}) => {
                commit('updateNewsUnread', data.payload)
            });
        },
        getRandomBanners({commit}) {
            return axios.get(`/api/misc/randomBanners`).then(({data}) => {
                commit('updateBanners', data.payload)
            });
        },
        incrementClickForBanner({commit}, id) {
            axios.get(`/api/misc/randomBanners/${id}/incrementNumClicked`)
        },
        /**
         * Loads broadcasts and records failure state without presenting stale rows as current results.
         * @param {Object} context Vuex state and commit boundary.
         * @param {Object} payload Search parameters, defaulting to an empty search.
         * @returns {Promise<void>} Resolves after success or a visible HTTP/network failure; performs one read.
         */
        async getBroadcasts({state, commit}, payload = {}) {
            // Retain the requested conditions and reset the previous failure before a fresh read.
            state.lastSearch.broadcasts = payload
            commit("updateBroadcastSearchError", "")

            // Treat rejected pages and malformed responses as failures rather than empty successful searches.
            try {
                const {data, status} = await axios.get("/api/broadcasts", {params: payload})

                // Keep recovery controls available even when a hybrid page exceeds its window.
                commit("updateBroadcastSearchOptions", data.searchOptions)

                // Reject failure payloads before replacing the visible result population.
                if (status >= 400 || data.status !== 200 || !Array.isArray(data.payload?.results)) {
                    commit("updateBroadcasts", {results: [], numResults: 0})
                    commit("updateBroadcastSearchError", data.message || "Search could not be completed. Please try again.")
                    return
                }

                // Replace visible results only with a valid response, including a genuine empty result set.
                commit("updateBroadcasts", data.payload)
            } catch (error) {
                // Network failures may be rejected as strings by the application interceptor.
                commit("updateBroadcasts", {results: [], numResults: 0})
                commit("updateBroadcastSearchError", "Search could not be completed. Please try again.")
            }
        },

        getBroadcast({commit, state}, id) {

            // getCompany is called in pre-hook, so we need to make sure user is bootstrapped. (This is done in app.created, which is called later)
            commit('bootstrapUserFromLocalStorage')

            return axios.get(`/api/broadcasts/${id}`).then(({data}) => {
                if (data.status !== 200) return {status: data.status,}

                // // let's split the body in two parts, the header with the broadcast fields, and the rest with the body and footer.
                // let msgElems = data.result.message.split(/\r?\n{2,}(.*)/s) // we split by double new line, but only once right after the header, to prevent the rest of the body from being split by double lines
                // let msgHeader = msgElems[0].replace(/(?:\r\n|\r|\n)/g, '<br>')
                // if (!msgElems[1]) msgElems[1] = ''
                // let msgRestBody = msgElems[1].replace(/(?:\r\n|\r|\n)/g, '<br>')

                // let urlRegex = /(\b(www\.?)[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/ig;
                // let urlRegexProdLink = /(?![^<]*>|[^<>]*<\/)((https?):\/\/[a-z0-9&#=./\-?_]+)/gi;
                // // msgHeader = msgHeader.replace(/(http?|https?):\/\/www./ig, 'www.')
                // msgHeader = msgHeader.replace(urlRegexProdLink, "<a href='$1?utm_source=thebrokersite&utm_medium=broadcast&utm_campaign=TBS-Membership' target=\"_blank\" rel=\"noopener\" class=\"text-red-700\">$1</a>")
                // msgRestBody = msgRestBody.replace(/(http?|https?):\/\/www./ig, 'www.')
                // msgRestBody = msgRestBody.replace(urlRegex, "<a href='https://$1' target=\"_blank\" rel=\"noopener\" class=\"text-red-700\">$1</a>")

                let rawMessage = data.result.message;
                const hasPicturesLink = /class=["']broadcast-pictures-link["']/i.test(rawMessage);

                let msgHeader = '';
                let msgRestBody = '';
                
                if (hasPicturesLink) {
                    const splitIndex = rawMessage.search(/<a[^>]*broadcast-pictures-link/i);

                    msgHeader = rawMessage.substring(0, splitIndex);
                    msgRestBody = rawMessage.substring(splitIndex);

                    // remove the pictures link from body
                    msgRestBody = msgRestBody.replace(
                        /<a[^>]*broadcast-pictures-link[^>]*>[\s\S]*?<\/a>\s*/gi,
                        ''
                    );
                    msgHeader = msgHeader.replace(/(<br\s*\/?>\s*){1,2}$/i, '');

                    
                } else {
                    let msgElems = rawMessage.split(/\r?\n{2,}(.*)/s);
                    msgHeader = msgElems[0];
                    msgRestBody = msgElems[1] || '';
                }
                // convert line breaks after splitting
                msgHeader = msgHeader.replace(/(?:\r\n|\r|\n)/g, '<br>');
                msgRestBody = msgRestBody.replace(/(?:\r\n|\r|\n)/g, '<br>');
                
                msgHeader = msgHeader.replace(/(<br\s*\/?>\s*)(?=<br\s*\/?>\s*$)/i, '');

                // Function to replace plain text URLs but ignore those already inside <a> tags
                function convertPlainUrlsToLinks(text) {
                    // First, temporarily replace existing links with a placeholder
                    const links = [];
                    const textWithoutLinks = text.replace(/<a\b[^>]*>.*?<\/a>/gi, match => {
                        links.push(match);
                        return `##LINK${links.length - 1}##`;
                    });

                    // Convert plain URLs to links
                    const processedText = textWithoutLinks.replace(
                        /(\b(https?:\/\/|www\.)[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi,
                        (match, url) => {
                            let href = url.startsWith("http") ? url : `https://${url}`;
                            return `<a href="${href}" target="_blank" rel="noopener" class="text-red-700">${url}</a>`;
                        }
                    );

                    // Restore original links
                    return processedText.replace(/##LINK(\d+)##/g, (match, index) => links[index]);
                }

                function convertAndTruncateUrl(text, maxLength = 40) {
                    // First, convert plain URLs using your existing function
                    let converted = convertPlainUrlsToLinks(text);

                    // Then, truncate the visible text if it's too long
                    converted = converted.replace(/<a\b[^>]*>(.*?)<\/a>/gi, (match, linkText) => {
                        // Only truncate if linkText exceeds maxLength
                        if (linkText.length > maxLength) {
                            // Extract the href from the original <a> tag
                            const hrefMatch = match.match(/href="([^"]*)"/i);
                            const href = hrefMatch ? hrefMatch[1] : linkText;

                            // Truncate text and add ellipsis
                            const truncatedText = linkText.slice(0, maxLength) + '...';

                            // Rebuild the <a> tag with original href but truncated text
                            return `<a href="${href}" target="_blank" rel="noopener" style="color: #c10007">${truncatedText}</a>`;
                        }
                        return match; // no change if short enough
                    });

                    return converted;
                }


                // Process Header (Preserve existing links and convert plain URLs)
                msgHeader = convertAndTruncateUrl(msgHeader);

                // Process Body (Convert only plain text URLs)
                msgRestBody = convertPlainUrlsToLinks(msgRestBody);

                // Then add <br> before each <p> tag
                msgRestBody = msgRestBody.replace(/<p/g, '<br><p');
                // msgRestBody = msgRestBody.replace(/<a class="broadcast-pictures-link".*?<\/a>/, '');
                msgRestBody = msgRestBody.replace(
                /<a\s+[^>]*class=["']broadcast-pictures-link["'][^>]*>[\s\S]*?<\/a>(<br\s*\/?>\s*){0,2}/gi,
                ''
                );


                let msgAutoLinked = msgHeader + "<br><br>" + msgRestBody


                return {
                    status: 200,
                    broadcast: _.defaults(data.result, {
                        broadcastListName: state.broadcastMap[data.result.listId]?.name,
                        messageFormatted: msgAutoLinked
                    })
                };
            });
        },
        /**
         * Reads complete enhanced matches in server rank order with one request.
         * @param {Object} context Vuex action context.
         * @param {Object} payload Source broadcastId; server owns all eligibility and ranking.
         * @returns {Promise<Object>} Existing status/payload response without percentages.
         * @throws {Error} On HTTP failure; the caller owns optional-modal recovery.
         */
        async getBroadcastPossibleMatches(context, {broadcastId}) {
            // Keep matching on its single enhanced boundary; no per-result detail requests.
            const {data} = await axios.get(`/api/broadcasts/possibleMatches/${broadcastId}`);
            return data;
        },
        getParts({state, commit}, payload = {}) {
            state.lastSearch.parts = payload
            return axios.get(`/api/parts`, {params: payload}).then(({data}) => {
                commit('updateParts', data.payload)
            });
        },
        getPart({commit}, id) {

            // getCompany is called in pre-hook, so we need to make sure user is bootstrapped. (This is done in app.created, which is called later)
            commit('bootstrapUserFromLocalStorage')

            return axios.get(`/api/parts/${id}`).then(({data}) => {
                if (data.status !== 200) return {status: data.status}
                return {
                    status: 200,
                    part: _.defaults(data.result, {
                        contactsCombined: _.map(data.result.contacts, "name").join(", "),
                    })
                };
            });
        },
        getPartStat({commit}, partNumber) {
            // this is where we check if a part is in the part landing table. If not, we return part = null to trigger a page not found
            return axios.get(`/api/parts/landing/${partNumber}`).then(({data}) => {
                if (data.status !== 200) return {part: null}
                return {
                    status: 200,
                    result: data.result
                }
            })
        },

        getInventory({state, commit}, payload = {}) {
            state.lastSearch.inventory = payload
            return axios.get(`/api/inventory`, {params: payload}).then(({data}) => {
                commit('updateInventory', data.payload)
            });
        },

        getInventoryCount(_, payload = {}) {
            return axios.get('/api/inventory/count');
        },

        getInventoryHits({commit}, {page}) {
            return axios.get(`/api/inventory/hits?page=${page}`).then(({data}) => {
                commit('updateInventoryHits', data.payload)
            });
        },

        getCompanies({state, commit}, payload = {}) {
            state.lastSearch.companies = payload
            return axios.get(`/api/companies`, {params: payload}).then(({data}) => {
                const payloadData = data.payload;
                if (payloadData && payloadData.results) {
                    commit('updateCompanies', data.payload)
                }
            });
        },
        async getNews({state, commit, dispatch}, payload = {}) {
            try {
                const isSecondPart = payload.isSecondPart;

                if (isSecondPart) {
                    payload = _.extend({isSecondPart: true}, state.newsPayload);
                } else {
                    commit('updateIsFetchingNews', true);
                    commit('updatehasLoadedSecondPart', false);
                    commit('updateNewsPayload', payload);
                }
                cleanupExpiredCacheEfficient();

                const cacheKey = generateCacheKey(state.newsPayload);
                const cachedData = await getCachedNews(cacheKey);

                // Check cache first
                if (!cachedData && !isSecondPart) {
                    dispatch('getNewsSummary', payload);
                }

                if (cachedData) {
                    commit('updateNews', {
                        results: cachedData.results,
                        numResults: cachedData.numResults
                    });
                    commit('updateIsFetchingNews', false);
                    commit('updatehasLoadedSecondPart', true);
                    commit('updateIsNewsSummary', false);
                    return cachedData;
                }

                // Fetch from API if no cache
                const {data} = await axios.get('/api/news', {params: payload});
                let mergedResults;
                if (isSecondPart) {
                    commit('updatehasLoadedSecondPart', true);
                    mergedResults = [...(state.news || []), ...(data.payload.results || [])];
                } else {
                    commit('updateHasLoadedFirstPart', true);
                }

                // Update store
                commit('updateNews', {
                    results: data.payload.results,
                    numResults: data.payload.numResults,
                    isSecondPart
                });
                commit('updateIsNewsSummary', false);

                // Cache the response (fire and forget)
                // Only cache the news data after fetching the second part
                if (isSecondPart) {
                    cacheNewsData(cacheKey, payload, {
                        results: mergedResults,
                        numResults: data.payload.numResults
                    }).catch(error => {
                        commit('updateIsFetchingNews', false);
                        console.warn('Background caching failed:', error);
                    });
                }
                commit('updateIsFetchingNews', false);
                return data.payload;

            } catch (error) {
                commit('updateIsFetchingNews', false);
                console.error('Error fetching news:', error);
                throw error;
            } finally {
                commit('updateIsFetchingNews', false);
            }
        },
        async getNewsSummary({state, commit}, payload = {}) {
            try {
                const {data} = await axios.get('/api/news/news-summary', {params: payload});
                if (!state.hasLoadedFirstPart && !state.hasLoadedSecondPart) {
                    commit('updateNews', {
                        results: data.payload.results,
                        numResults: data.payload.numResults,
                    });
                    commit('updateIsNewsSummary', true);
                    commit('updateIsFetchingNews', false);
                }
                return data.payload;

            } catch (error) {
                console.error('Error fetching news:', error);
                throw error;
            }
        },
        getNewsPost({commit}, id) {
            return axios.get(`/api/news/${id}`).then(({data}) => {
                if (data.status !== 200) {
                    return Promise.reject(new Error('News post not found or invalid status'));
                }

                commit('updateNewsPost', data.result)

                const categoryUrl = data.category_url;
                const titleSlug = data.result.titleSlug;
                if (categoryUrl && titleSlug) {
                    const newUrl = `/news/${categoryUrl}/${titleSlug}?id=${data.result.id}`;
                    window.history.replaceState({}, '', newUrl);
                }
            });
        },
        getLatestPosts({}, country = null) {
            const cacheKey = 'latestPosts';
            const cacheExpiry = 10 * 60 * 1000; // 30 minutes 

            // Check if cached data exists and is still valid
            const cachedData = localStorage.getItem(cacheKey);
            if (cachedData) {
                const {data, timestamp} = JSON.parse(cachedData);

                // Check if cache is still valid (not expired)
                if (Date.now() - timestamp < cacheExpiry) {
                    return Promise.resolve(data);
                }
            }

            // If no valid cache, make API call
            return axios.get(`/api/news/latest`).then(({data}) => {
                // Cache the response with timestamp
                const cacheData = {
                    data: data,
                    timestamp: Date.now()
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));
                return data;
            });
        },
        getCompany({commit, dispatch}, idOrOptions) {

            // getCompany is called in pre-hook, so we need to make sure user is bootstrapped. (This is done in app.created, which is called later)
            commit('bootstrapUserFromLocalStorage')

            const id = typeof idOrOptions === 'object' ? idOrOptions.id : idOrOptions;
            const fromServices = typeof idOrOptions === 'object' && idOrOptions.fromServices;
            const params = fromServices ? {fromServices: '1'} : {};

            return axios.get(`/api/companies/${id}`, {params}).then(({data}) => {
                if (data.status !== 200) return {status: data.status}
                return storeUtils.enrichProfile(data)
            });
        },
        getProfile({commit, dispatch}) {

            // getCompany is called in pre-hook, so we need to make sure user is bootstrapped. (This is done in app.created, which is called later)
            commit('bootstrapUserFromLocalStorage')

            return axios.get(`/api/companies/me`).then(({data}) => {
                if (data.status !== 200) return {status: data.status}
                return storeUtils.enrichProfile(data)
            });
        },
        getCountries({commit}, payload = {}) {
            return axios.get(`/api/companies/countries`).then(({data}) => {
                commit('updateCountries', data.payload)
            });
        },
        getCountriesAll({commit}, payload = {}) {
            return axios.get(`/api/misc/countriesAll`).then(({data}) => {
                commit('updateCountriesAll', data.payload)
            });
        },

        getMarkers({commit}, payload = {}) {
            return axios.get(`/api/companies/markers`).then(({data}) => {
                commit('updateMarkers', data.payload)
            });
        },

        getCountryCoords({}, country = null) {
            return axios.get(`/api/companies/countryCoords?country=${country}`).then(({data}) => data);
        },
        getTBSTestimonials({commit}) {
            return axios.get(`/api/testimonials/tbs`).then(({data}) => {
                commit('updateTestimonials', data.testimonials)
            })
        },

        signout({commit, state}) {
            const sessionId = localStorage.getItem('sessionId');
            if (sessionId) {
                try {
                    const response = axios.post(`/api/auth/logout`, {
                        sessionId,
                    });

                    if (response.status !== 200) {
                        console.error(response.status, response)
                    }

                    localStorage.removeItem('sessionId');
                } catch (error) {
                    console.error(error);
                }
            }

            commit('removeUser')
        },
        async signin({commit, state}, payloadIn = {}) {
            const response = await axios.post(`/api/auth/login`, payloadIn);

            // Check for a non-200 status, which indicates an error
            if (response.status !== 200) {
                const errorMessage = response.data?.payload?.msg || 'Login failed. Please try again.';
                const error = new Error(errorMessage);
                error.status = response.status;
                throw error;
            }

            const user = response.data;

            // Check for 2FA requirement
            if (user.requires2FA) {
                return response; // Return for the 2FA flow
            }

            // If no 2FA, commit the user data to update the state
            commit('updateUser', user);

            // Save the session ID
            try {
                const sessionId = response?.data?.sessionId;
                if (sessionId) {
                    localStorage.setItem('sessionId', sessionId);
                }
            } catch (error) {
                console.error(error);
            }

            return response;
        },
        updateCompany({commit, state}, payload = {}) {
            return axios.post(`/api/companies/updateCompany`, payload)
        },
        updateUserPossibleMatched({commit, state}, possibleMatchesDisabled = false) {
            return axios.post(`/api/companies/actions/setPossibleMatches`, {possibleMatchesDisabled})
        },
        register({commit, state}, payload = {}) {
            return axios.post(`/api/companies`, payload)
        },
        updateContact({commit, state}, payload = {}) {
            return axios.post(`/api/companies/updateContact`, payload)
        },
        setAsFavorite({commit, state}, payload = {}) {
            return axios.post(`/api/companies/setCompanyAsFavorite`, payload)
        },
        requestPassword({commit, state}, payload = {}) {
            return axios.post(`/api/companies/requestPassword`, payload)
        },
        verifyResetToken({commit, state}, payload = {}) {
            return axios.post(`/api/companies/verifyResetToken`, payload)
        },
        resetPassword({commit, state}, payload = {}) {
            if (payload.token) return axios.post(`/api/companies/resetPasswordWithToken`, payload)
            return axios.post(`/api/companies/resetPassword`, payload)
        },
        getBroadcastSettings({commit, state}) {
            return axios.get(`/api/companies/me/broadcastSettings`).then(({data}) => data.payload);
        },
        updateBroadcastSettings({commit, state}, payload) {
            return axios.post(`/api/companies/me/broadcastSettings`, payload)
        },
        getDiamondMembers({}, country = null) {
            return axios.get(`/api/companies/diamondMembers`).then(({data}) => data);
        },
        getGoldMembers({}, country = null) {
            return axios.get(`/api/companies/goldMembers`).then(({data}) => data);
        },
        getTrustedMembers({}, country = null) {
            return axios.get(`/api/companies/trustedMembers`).then(({data}) => data);
        },
        getFavoriteMembers({}, country = null) {
            return axios.get(`/api/companies/favoriteMembers`).then(({data}) => data);
        },
        sendMailToTBS({commit, state}, payload = {}) {
            return axios.post(`/api/mail/sendMailToTBS`, payload)
        },
        sendMailToContact({commit, state}, payload = {}) {
            return axios.post(`/api/mail/sendMailToContact`, payload)
        },
        sendBroadcastReply({commit, state}, payload = {}) {
            return axios.post(`/api/mail/sendBroadcastReply`, payload)
        },
        sendRFQ({commit, state}, payload = {}) {
            return axios.post(`/api/mail/sendRFQ`, payload)
        },
        createBroadcastV2({commit, state}, payload = {}) {
            return axios.post(`/api/broadcasts/v2`, payload)
        },
        generateAIDescription({commit, state}, payload = {}) {
            return axios.post(`/api/broadcasts/generateAIDescription`, payload)
        },
        deleteBroadcast({commit, state}, payload = {}) {
            return axios.delete(`/api/broadcasts/${payload.id}`)
        },
        resendBroadcast({commit, state}, payload = {}) {
            return axios.post(`/api/broadcasts/${payload.id}/actions/resend`)
        },
        getBroadcastLimit({commit, state}, payload = {}) {
            return axios.post(`/api/broadcasts/limit`, payload)
        },
        addPart({commit, state}, payload = {}) {
            return axios.post(`/api/inventory`, payload)
        },
        updatePart({commit, state}, payload = {}) {
            return axios.patch(`/api/inventory/${payload.id}`, payload)
        },
        deletePart({commit, state}, payload = {}) {
            return axios.delete(`/api/inventory/${payload.id}`)
        },
        deleteAllParts({commit, state}) {
            return axios.post(`/api/inventory/actions/deleteAll`)
        },
        setPageSize({commit, state}, pageSize) {
            localStorage.setItem('pageSize', pageSize);
            state.pageSize = pageSize;
        },
        requestInvoice({commit, state}, payload = {}) {
            return axios.post(`/api/companies/invoice`, payload)
        },
        uploadLogo({commit, state}, payload = {}) {
            return axios.post(`/api/companies/uploadLogo`, payload)
        },
        removeLogo({commit, state}) {
            return axios.post(`/api/companies/removeLogo`)
        },
        uploadPicture({commit, state}, payload = {}) {
            return axios.post(`/api/companies/uploadPicture`, payload).then(resp => {
                commit('updateProfilePic', resp.data.profilePic)
                return resp
            })
        },
        removePicture({commit, state}) {
            return axios.post(`/api/companies/removePicture`).then(resp => {
                commit('removeProfilePic')
                return resp;
            })
        },
        loadProfilePicture({commit, state}) {
            return axios.get(`/api/companies/fetchPicture`).then(resp => {
                if (resp.data.profilePic) {
                    commit('updateProfilePic', resp.data.profilePic)
                } else {
                    commit('removeProfilePic')
                }
            })
        },
        logPage({commit, state}, payload = {}) {

            // logPage is sometimes called in pre-hook, so we need to make sure user is bootstrapped. (This is done in app.created, which is called later)
            commit('bootstrapUserFromLocalStorage')
            return axios.post(`/api/misc/logPage`, payload)
        },
        recommendCompany({commit, state}, payload = {}) {
            const p = _.omit(payload, ['companyId'])
            return axios.post(`/api/companies/${payload.companyId}/actions/recommendCompany`, p)
        },
        deleteRec({commit, state}, payload = {}) {
            return axios.post(`/api/companies/me/actions/deleteRecommendation`, payload)
        },
        approveRec({commit, state}, payload = {}) {
            return axios.post(`/api/companies/me/actions/approveRecommendation`, payload)
        },
        fetchDoUnapprovedRecsExist({state}) {
            return axios.get(`/api/companies/me/actions/doUnapprovedRecommendationsExist`).then(({data}) => {
                state.doUnapprovedRecsExist = !!data.payload
            });
        },

        // saved searches
        getSavedSearches({commit}) {
            return axios.get(`/api/savedSearches`).then(({data}) => {
                commit('updateSavedSearches', data)
            });
        },
        async switchSavedSearchNotificationType({commit}, item) {
            const resp = await axios.post(`/api/savedSearches/${item.id}/actions/updateNotificationType`, {isDigest: !item.isDigest});
            if (resp.data.status !== 200) throw new Error("Error updating saved search")

            await axios.get(`/api/savedSearches`).then(({data}) => {
                commit('updateSavedSearches', data)
            });
        },
        async removeSavedSearch({commit}, item) {
            const resp = await axios.delete(`/api/savedSearches/${item.id}`);
            if (resp.data.status !== 200) throw new Error("Error removing saved search")

            await axios.get(`/api/savedSearches`).then(({data}) => {
                commit('updateSavedSearches', data)
            });
        },

        async persistSavedSearch({commit}, item) {
            const resp = await axios.post(`/api/savedSearches`, item);
            if (resp.data.status !== 200) throw new Error("Error saving saved search")

            await axios.get(`/api/savedSearches`).then(({data}) => {
                commit('updateSavedSearches', data)
            })
        },

        getLastInvoiceStatus({commit}, payload = {}) {
            // return axios.get(`/api/companies/getLastInvoiceStatus`).then(({data}) => {
            //     commit('updateLastInvoiceStatus', data.payload)
            // });

            return axios.get(`/api/companies/getLastInvoiceStatus`)
            .then(({data}) => {
                commit('updateLastInvoiceStatus', data.payload)
            })
            .catch(error => {
                if (error !== 'Network Error') {
                    throw error
                }

                // Check if the API is still reachable
                return axios.get('/api/testimonials/tbs')
                    .then(() => {

                        // API is reachable, but the original request failed.
                        return axios.post('/api/events/request-blocked', {
                            userAgent: navigator.userAgent
                        })
                    })
                    .catch(() => {
                        // API itself is unavailable, so don't report anything.
                    })
            })
        },

        getInvoices({commit}) {
            return axios.get(`/api/companies/getInvoices`).then(({data}) => {
                commit('updateInvoices', data.invoices)
                commit('updateInvoicesEBM', data.invoicesEBM)
            });
        },

        getCreditNotes({commit}) {
            return axios.get(`/api/companies/getCreditNotes`).then(({data}) => {
                commit('updateCreditNotes', data.creditNotes)
            });
        },

        downloadPDF({}, invoice) {
            return axios({
                url: `/api/companies/actions/downloadInvoiceAsPDF/${invoice.invoiceNumber}`,
                method: 'GET',
                responseType: 'blob', // important
            }).then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `invoice - ${invoice.invoiceNumber}.pdf`);
                document.body.appendChild(link);
                link.click();
            });
        },

        downloadPDFCn({}, creditNote) {
            return axios({
                url: `/api/companies/actions/downloadCreditNoteAsPDF/${creditNote.invoiceNumber}`,
                method: 'GET',
                responseType: 'blob', // important
            }).then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `creditnote - ${creditNote.invoiceNumber}.pdf`);
                document.body.appendChild(link);
                link.click();
            });
        },

        uploadComplaint({}, {companyId, formData}) {
            return axios.post(`/api/companies/${companyId}/actions/logComplaint`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
        },
        trackGA4Event(context, payload) {
            const {tbsSpecialUsers} = context.state;
            const lastUserID = localStorage.getItem("lastUserID");

            if (tbsSpecialUsers.includes(lastUserID)) {
                console.log('blocked')
                return;
            }

            try {
                const referrer = localStorage.getItem("referrer") || "Direct";

                const eventData = {
                    event: payload.event,
                    event_category: payload.event_category,
                    event_label: payload.event_label,
                    action: payload.action,
                    message: payload.message,
                    membership: payload.membership,
                    user_id: payload.user_id || context.state.user?.id || null,
                    heard_through: payload.heardThrough,
                    step: payload.step,
                    search_term: payload.search_term || '',
                    referrer: referrer || 'Direct',
                    external_exit: payload.external_exit,
                    debug_mode: true,
                };

                // Send event to GA
                gtag('event', eventData.event, eventData);

                // axios.post('/api/events', eventData).then(() => {
                //     console.log(`Event logged.`);
                // })
                //     .catch(err => {
                //         console.error('Failed to log event');
                //     });
            } catch (error) {
                throw new Error(error.message);
            }
        },
        /**
         * Fetches price statistics for the current Broadcast Search filter context.
         * @param {Object} context Vuex action context.
         * @param {Object} [payload={}] Search query/type/category/company/favorites/exact/country/own filters, selected sort and saved lexical context.
         * @returns {Promise<Object|null>} Existing API response, null after a logged HTTP failure.
         * @effects Performs one GET; writes no search state or external data.
         */
        getPricingData(context, payload = {}) {
          // Forward eligibility unchanged so prices describe the user's current Search context.
          const { query, listType, type, companyId, favorites, exactPhrase, country, onlyOwnBroadcasts, savedSearch, sort } = payload
          const params = {
            query,
            listType,
            type,
            companyId,
            favorites,
            exactPhrase,
            country,
            onlyOwnBroadcasts,
            savedSearch,
            sort,
          }

          // Preserve the existing response and logged-failure contract for the price modal.
          return axios
            .get(`/api/broadcasts/pricingdata`, { params })
            .then(({ data }) => data)
            .catch(err => {
              console.error("Failed to fetch pricing data", err)
              return null
            })
        },
        checkNewsCacheValidity({state}) {
            const newsLastUpdated = state.settingsMap.lastNewsUpdate || '';
            const currentNewsUpdate = localStorage.getItem('newsLastUpdated') || '';

            localStorage.setItem('newsLastUpdated', newsLastUpdated);

            if (newsLastUpdated !== currentNewsUpdate) {
                localStorage.removeItem('latestPosts');
                return clearAllCacheEntries()
                    .then(() => {
                        console.log('Cache cleared');
                    })
                    .catch(error => {
                        console.error('Failed to clear cache:', error);
                    });
            } else {
                return Promise.resolve();
            }
        },
        initiateEscrow({}, payload = {}) {
            return axios.post(`/api/escrow/`, payload)
        },
        createPurchaseOrder({}, payload = {}) {
            return axios.post(`/api/purchaseOrder`, payload)
        },
        resetNewsUnread({ commit }) {
            // Commit a mutation to reset the state
            commit('updateNewsUnread', 0);

            // Remove from localStorage if it exists
            localStorage.removeItem('newsUnread');
        },
        resetUnreadNewsCount({}, payload = {}) {
            return axios.post(`/api/news/reset-unread-news-count`, payload)
        },
        getPurchaseOrders({state, commit}, payload = {}) {
            commit('updateFetchingPurchaseOrders', true)
            return axios.get(`/api/purchaseOrder`, {params: payload}).then(({data}) => {
                const payloadData = data.payload;
                if (payloadData && payloadData.results) {
                    commit('updatePurchaseOrders', data.payload)
                    commit('updateFetchingPurchaseOrders', false)
                }
            });
        },
    },
    modules: {}
})

